import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Divider,
} from "@mui/material";
import { ArrowForwardIosRounded, OpenInNewRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { adminRoutes } from "./adminRoutes";
import colors from "../../Constants/colors";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation("adminDashboard");

  const monitoring = adminRoutes.find(
    (section) => section.layout === "monitoring",
  );

  const sections = adminRoutes.filter(
    (section) => section.layout !== "monitoring",
  );

  const handleAction = (action: any) => {
    if (action.externalUrl) {
      window.open(action.externalUrl, "_blank", "noopener,noreferrer");
    } else if (action.path) {
      navigate(action.path);
    }
  };

  const renderAction = (action: any, dark = false) => {
    const Icon = action.icon;

    return (
      <Box
        key={action.path ?? action.externalUrl}
        onClick={() => handleAction(action)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.25,
          py: 1,
          borderRadius: 1.5,
          cursor: "pointer",
          bgcolor: dark ? "rgba(255,255,255,.05)" : "transparent",
          "&:hover": {
            bgcolor: dark ? "rgba(255,255,255,.1)" : colors.gray50,
          },
        }}
      >
        {Icon && (
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.5,
              bgcolor: dark ? "rgba(255,255,255,.08)" : colors.gray100,
              color: dark ? colors.white : colors.primary_dark,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 16 }} />
          </Box>
        )}

        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 12.5,
            fontWeight: 600,
            color: dark ? colors.white : colors.primary_dark,
          }}
        >
          {t(action.titleKey)}
        </Typography>

        {action.externalUrl ? (
          <OpenInNewRounded
            sx={{
              fontSize: 14,
              color: dark ? "rgba(255,255,255,.5)" : colors.gray400,
            }}
          />
        ) : (
          <ArrowForwardIosRounded
            sx={{
              fontSize: 11,
              color: dark ? "rgba(255,255,255,.5)" : colors.gray400,
            }}
          />
        )}
      </Box>
    );
  };

  const renderSection = (section: any) => {
    const Icon = section.icon;

    return (
      <Card
        key={section.titleKey}
        sx={{
          height: "100%",
          borderRadius: 2.5,
          border: `1px solid ${colors.gray200}`,
          boxShadow: "0 2px 10px rgba(0,0,0,.035)",
        }}
      >
        <CardContent
          sx={{
            p: 2,
            "&:last-child": {
              pb: 2,
            },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={0.75}>
            {Icon && (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.75,
                  bgcolor: "rgba(3,46,77,.07)",
                  color: colors.primary_dark,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
            )}

            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: colors.primary_dark,
              }}
            >
              {t(section.titleKey)}
            </Typography>
          </Stack>

          <Typography
            sx={{
              color: colors.gray600,
              fontSize: 12,
              lineHeight: 1.4,
              mb: 1,
            }}
          >
            {t(section.descriptionKey)}
          </Typography>

          <Divider
            sx={{
              borderColor: colors.gray100,
              mb: 0.5,
            }}
          />

          <Stack spacing={0.1}>
            {section.actions.map((action: any) => renderAction(action))}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: colors.gray50,
        minHeight: "calc(100vh - 64px)",
        width: "100%",
        py: { xs: 2.5, md: 3 },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1900,
          mx: "auto",
          px: { xs: 2, sm: 2.5, lg: 3 },
        }}
      >
        <Box
          mb={{ xs: 2.5, md: 3 }}
          sx={{
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 25, md: 30 },
              fontWeight: 800,
              color: colors.primary_dark,
              letterSpacing: "-0.7px",
            }}
          >
            {t("adminDashboard")}
          </Typography>

          <Typography
            sx={{
              mt: 0.5,
              fontSize: 13.5,
              color: colors.gray600,
            }}
          >
            {t("adminDashboardDescription")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(250px, 0.7fr) minmax(0, 2.3fr)",
              xl: "minmax(280px, 0.7fr) minmax(0, 2.3fr)",
            },
            gap: { xs: 1.75, md: 2 },
            alignItems: "stretch",
          }}
        >
          {monitoring && (
            <Card
              sx={{
                gridColumn: { xs: 1, md: 1 },
                height: "100%",
                alignSelf: "stretch",
                borderRadius: 2.75,
                bgcolor: colors.primary_dark,
                color: colors.white,
                border: 0,
                boxShadow: "0 10px 35px rgba(3,46,77,.16)",
              }}
            >
              <CardContent
                sx={{
                  p: 2.25,
                  "&:last-child": {
                    pb: 2.25,
                  },
                }}
              >
                {(() => {
                  const Icon = monitoring.icon;

                  return (
                    <>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          py: 1,
                        }}
                      >
                        {Icon && (
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: 2,
                              bgcolor: "rgba(255,255,255,.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Icon sx={{ fontSize: 19 }} />
                          </Box>
                        )}

                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 700,
                          }}
                        >
                          {t(monitoring.titleKey)}
                        </Typography>
                      </Stack>
                      <Divider
                        sx={{
                          borderColor: "rgba(255,255,255,.1)",
                          my: 2,
                        }}
                      />
                      <Stack spacing={1}>
                        {monitoring.actions.map((action: any) =>
                          renderAction(action, true),
                        )}
                      </Stack>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          <Box
            sx={{
              gridColumn: { xs: 1, md: 2 },
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: { xs: 1.75, md: 2 },
              alignItems: "stretch",
            }}
          >
            {sections.map(renderSection)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
